import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/api/engine';
import type { SettingsResponse } from '@/api/types';
import { SettingsProvider, type TierLevel } from '@seitiate/settings-ui';
import { SettingsSpineContent } from '@/components/settings/SettingsSpineContent';
import {
  ALL_SETTINGS_NAV_ITEMS,
  SETTINGS_NAV_GROUPS,
  normalizeSettingsRouteSection,
  titleCase,
  type SettingsRouteSectionId,
} from '@/components/settings/settingsSpineConfig';
import { useSurfaces } from '@/hooks/useSurfaces';
import { OPERATOR_SETTINGS_PANELS, isSettingsPanelVisible } from '@/lib/surfaceManifest';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  notificationPreferencesLookCustom,
} from '@/components/settings/notificationPreferencesDefaults';

// The settings-ui package's TierLevel union is the legacy ladder
// (sense/making/meaning/mastery/sovereign) and gates panels off it. The platform
// base is now Free / Amateur / Professional (Option-B re-home), so we accept the
// new names AND map them onto the equivalent TierLevel so Governor/Canon gating
// still fires. Vertical tier keys (sakme_*/sensarra_*) are not platform tiers and
// pass through as `undefined` (the engine's visible_categories gates those).
const KNOWN_TIERS: readonly string[] = [
  // Legacy platform ladder (still honored for existing accounts).
  'sense', 'making', 'meaning', 'mastery', 'sovereign',
  // New platform base tiers.
  'free', 'amateur', 'professional',
];

// New platform tier -> the legacy TierLevel its capabilities map to.
//   free        -> sense    (base; Governor/Canon gated out)
//   amateur     -> making   (Making+ unlocks creative engine, N1/N2)
//   professional-> mastery  (Mastery+ unlocks kill switches; Meaning+ Canon)
const PLATFORM_TIER_TO_LEVEL: Record<string, TierLevel> = {
  free: 'sense',
  amateur: 'making',
  professional: 'mastery',
};

const SELF_HEADED_SECTIONS: readonly SettingsRouteSectionId[] = ['models', 'costs'];

function normalizeTier(raw: string | undefined): TierLevel | undefined {
  if (!raw) return undefined;
  if (raw in PLATFORM_TIER_TO_LEVEL) return PLATFORM_TIER_TO_LEVEL[raw];
  return KNOWN_TIERS.includes(raw) ? (raw as TierLevel) : undefined;
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { sectionId, subSectionId } = useParams<{ sectionId?: string; subSectionId?: string }>();
  const activeSection: SettingsRouteSectionId = normalizeSettingsRouteSection(sectionId);

  // Deep-link nested sub-sections (e.g., /settings/models/providers) to the
  // corresponding anchor on the panel. The panels set anchorId=section-<id>-<sub>-...
  // conventions, so scrolling to `#section-${sectionId}-${subSectionId}-dashboard`
  // (or the panel's first documented anchor) is the closest generic mapping.
  useEffect(() => {
    if (!subSectionId) return;
    const anchorCandidates = [
      `section-${activeSection}-${subSectionId}-dashboard`,
      `section-${activeSection}-${subSectionId}`,
    ];
    const tryScroll = () => {
      for (const id of anchorCandidates) {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
      }
      return false;
    };
    // Panels render async (settings fetch); poll briefly for the anchor.
    let tries = 0;
    const interval = window.setInterval(() => {
      tries += 1;
      if (tryScroll() || tries > 20) window.clearInterval(interval);
    }, 100);
    return () => window.clearInterval(interval);
  }, [activeSection, subSectionId]);

  const activeItem = ALL_SETTINGS_NAV_ITEMS.find((item) => item.id === activeSection) ?? ALL_SETTINGS_NAV_ITEMS[0];

  // Backend-resolved surface set: operator/internal settings panels (agents,
  // runtime) are hidden from the customer subset. Filter the sidebar to what the
  // caller may see, and gate the active panel directly (nav filtering alone is not
  // enough — a customer could deep-link /settings/agents).
  const { surfaces, settled } = useSurfaces();
  const visibleGroups = useMemo(
    () =>
      SETTINGS_NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => isSettingsPanelVisible(item.id, surfaces)),
      })).filter((group) => group.items.length > 0),
    [surfaces]
  );
  const activeGatedSurface = OPERATOR_SETTINGS_PANELS[activeSection];
  const activePanelAllowed = !activeGatedSurface || surfaces.has(activeGatedSurface);

  const [snapshot, setSnapshot] = useState<SettingsResponse | null>(null);
  const [notificationSummary, setNotificationSummary] = useState<'Default' | 'Custom' | 'Unknown'>('Unknown');

  useEffect(() => {
    api.settings
      .getAll()
      .then(setSnapshot)
      .catch(() => setSnapshot(null));
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const prefs = await api.notifications.getPreferences();
        const merged = { ...DEFAULT_NOTIFICATION_PREFERENCES, ...prefs };
        setNotificationSummary(notificationPreferencesLookCustom(merged) ? 'Custom' : 'Default');
      } catch {
        setNotificationSummary('Unknown');
      }
    };
    void load();
  }, []);

  const connectedCount = useMemo(
    () => snapshot?.connected_sources.filter((source) => source.connected).length ?? 0,
    [snapshot]
  );

  const notificationCardLabel =
    notificationSummary === 'Unknown' ? (snapshot ? 'See notifications panel' : 'Unknown') : notificationSummary;

  return (
    <SettingsProvider variant="creator" role="tenant" tier={normalizeTier(snapshot?.subscription_tier)}>
    <div className="min-h-screen px-6 py-8" style={{ background: 'var(--bg-primary)' }}>
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-[0.25em]" style={{ color: 'var(--text-muted)' }}>
              Canonical Settings Spine
            </div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Seity Settings
            </h1>
            <p className="max-w-3xl text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
              The machine room for identity, trust, memory, models, notifications, and governance. Modal and workbench entry
              points render the same panels; this route remains the canonical full-page settings home.
            </p>
          </div>
          <div className="rounded-xl border px-4 py-3 text-sm" style={{ borderColor: 'var(--border)', background: 'var(--bg-secondary)' }}>
            <div style={{ color: 'var(--text-primary)' }}>{snapshot?.persona_name || 'your assistant'}</div>
            <div style={{ color: 'var(--text-muted)' }}>{titleCase(snapshot?.subscription_tier || 'sense')} tier</div>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          <SummaryCard label="Connected sources" value={String(connectedCount)} />
          <SummaryCard label="Notifications" value={notificationCardLabel} />
          <SummaryCard label="Billing" value={titleCase(snapshot?.subscription_status || 'unknown')} />
          <SummaryCard label="Current panel" value={activeItem.label} />
        </div>

        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside
            className="self-start rounded-2xl border p-3 lg:sticky lg:top-6"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-secondary)' }}
          >
            {visibleGroups.map((group) => (
              <div key={group.title} className="mb-4 last:mb-0">
                <div className="mb-2 px-2 text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-muted)' }}>
                  {group.title}
                </div>
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const active = item.id === activeSection;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => navigate(item.id === 'seity' ? '/settings' : `/settings/${item.id}`)}
                        className="w-full rounded-xl px-3 py-3 text-left transition-colors"
                        style={{
                          background: active ? 'rgba(212, 175, 55, 0.12)' : 'transparent',
                          border: `1px solid ${active ? 'rgba(212, 175, 55, 0.28)' : 'transparent'}`,
                        }}
                      >
                        <div className="text-sm font-medium" style={{ color: active ? 'var(--accent-gold)' : 'var(--text-primary)' }}>
                          {item.label}
                        </div>
                        <div className="mt-1 text-xs leading-5" style={{ color: 'var(--text-muted)' }}>
                          {item.description}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </aside>

          <section className="overflow-hidden rounded-2xl border" style={{ borderColor: 'var(--border)', background: 'var(--bg-secondary)' }}>
            {SELF_HEADED_SECTIONS.includes(activeSection) ? null : (
              <div className="border-b px-6 py-5" style={{ borderColor: 'var(--border)' }}>
                <div className="text-[11px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-muted)' }}>
                  {activeItem.group}
                </div>
                <h2 className="mt-2 text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {activeItem.label}
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                  {activeItem.description}
                </p>
              </div>
            )}
            <div className="p-6">
              {activePanelAllowed ? (
                <SettingsSpineContent
                  variant="route"
                  activeSection={activeSection}
                  snapshot={snapshot}
                  navigate={navigate}
                />
              ) : !settled ? (
                <div className="py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                  Checking access…
                </div>
              ) : (
                <div className="py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                  This settings area is not available on your account.
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
    </SettingsProvider>
  );
}

const SummaryCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl border px-4 py-3" style={{ borderColor: 'var(--border)', background: 'var(--bg-secondary)' }}>
    <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-muted)' }}>
      {label}
    </div>
    <div className="mt-2 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
      {value}
    </div>
  </div>
);
